import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Send, Loader2, Check, MessageCircle, FileImage, FileText, Shield, UserPlus, LogIn } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import api from '../utils/api';

const CONTACT_OPTIONS = [
  { value: 'platform', label: 'In-app message' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone call' },
];

// FIND-H-08 · post-cutover browser auth is the HttpOnly `treatx_auth`
// cookie. Token retrieval helpers return null (kept for legacy call
// sites that only test truthiness).
function getPatientToken() {
  return null;
}

export default function QuoteRequestModal({ clinicId, clinicName, onClose, onSuccess, preselectedTreatment, preselectedCampaignId, campaignSelection }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // form | sending | success
  const [message, setMessage] = useState('');
  const [treatment, setTreatment] = useState('');
  const [resolvedTreatmentLabel, setResolvedTreatmentLabel] = useState('');
  const [contactPrefs, setContactPrefs] = useState(['platform', 'email', 'phone']);
  const [medicalFiles, setMedicalFiles] = useState([]);
  const [selectedFileIds, setSelectedFileIds] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [treatmentOptions, setTreatmentOptions] = useState([]);
  const [successData, setSuccessData] = useState(null);

  const token = getPatientToken();

  // Fetch treatments from backend taxonomy
  useEffect(() => {
    api.get('/public/treatments')
      .then(({ data: d }) => {
        const allTreats = (d.treatments || []).filter(t => t.isActive);
        const names = allTreats.map(t => t.name);
        names.push('Other');
        setTreatmentOptions(names);

        if (preselectedTreatment) {
          const norm = (s) => (s || '').replace(/[-_]/g, '_').toLowerCase();
          const pNorm = norm(preselectedTreatment);
          const match = allTreats.find(t =>
            t.slug === preselectedTreatment ||
            t.name === preselectedTreatment ||
            norm(t.slug) === pNorm ||
            (pNorm.length > 3 && norm(t.slug).startsWith(pNorm)) ||
            (norm(t.slug).length > 3 && pNorm.startsWith(norm(t.slug)))
          );
          if (match) {
            setTreatment(match.name);
            setResolvedTreatmentLabel(match.name);
          } else {
            const label = preselectedTreatment.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
            setTreatment(label);
            setResolvedTreatmentLabel(label);
          }
        }
      })
      .catch(() => setTreatmentOptions(['Other']));
  }, [preselectedTreatment]);

  // Fetch patient's medical files
  useEffect(() => {
    if (!token) return;
    setFilesLoading(true);
    api.get('/patient/medical-files')
      .then(({ data: d }) => setMedicalFiles(d.files || []))
      .catch(() => {})
      .finally(() => setFilesLoading(false));
  }, [token]);

  const toggleFile = (id) => {
    setSelectedFileIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // P0-6: Toggle contact preference
  const toggleContact = (value) => {
    setContactPrefs(prev => {
      if (prev.includes(value)) {
        // Don't allow deselecting all — keep at least one
        if (prev.length <= 1) return prev;
        return prev.filter(v => v !== value);
      }
      return [...prev, value];
    });
  };

  // P0-1: Save pending request to localStorage before login redirect
  const savePendingRequest = () => {
    localStorage.setItem('treatx_pending_quote', JSON.stringify({
      clinic_id: clinicId,
      clinic_name: clinicName,
      treatment: treatment || resolvedTreatmentLabel || '',
      message: message.trim(),
      contact_prefs: contactPrefs,
      timestamp: Date.now(),
    }));
  };

  const handleSubmit = async () => {
    if (!message.trim() || message.trim().length < 5) {
      toast.error('Message must be at least 5 characters');
      return;
    }
    if (!token) {
      // P0-1: Save request before redirecting to login
      savePendingRequest();
      toast.info('Please log in or create an account to send your request');
      navigate('/login', { state: { returnTo: `/clinics/${clinicId}`, requestQuote: true } });
      onClose();
      return;
    }
    setStep('sending');
    try {
      const { data } = await api.post(`/public/clinics/${clinicId}/request-quote`, {
        message: message.trim(),
        treatment_interest: treatment || undefined,
        preferred_contact: contactPrefs,
        medical_attachment_ids: selectedFileIds.length > 0 ? selectedFileIds : undefined,
        // OT-0F.1 — when the patient triggers the quote from a campaign CTA,
        // pass the immutable campaign_id so the backend can C4A-snapshot it
        // onto the lead. The existing handoff semantics are untouched.
        campaign_id: preselectedCampaignId || undefined,
        // SSO-A2 (audit defect D2) — this modal is opened by a real click on a
        // specific International Offer CTA, so the provenance must say so.
        // Without the token the backend correctly defaulted to the WEAKER
        // claim and recorded `patient_selected: false` for a deliberate choice.
        // Still only a client assertion — the backend trust boundary
        // (`_normalize_patient_selection`) is unchanged.
        campaign_selection: preselectedCampaignId
          ? (campaignSelection || 'explicit')
          : undefined,
      });
      if (data.success) {
        setStep('success');
        setSuccessData(data);
        localStorage.removeItem('treatx_pending_quote');
        onSuccess?.(data);
      } else {
        toast.error(data.detail || 'Failed to send request');
        setStep('form');
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        savePendingRequest();
        toast.error('Please log in to request a quote');
        navigate('/login', { state: { returnTo: `/clinics/${clinicId}`, requestQuote: true } });
        onClose();
      } else if (status === 503) {
        toast.error('Service temporarily unavailable. Please try again.');
        setStep('form');
      } else {
        toast.error(err.response?.data?.detail || err.message || 'Network error');
        setStep('form');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="quote-modal-overlay">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" data-testid="quote-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-slate-50/50">
          <div>
            <h3 className="text-sm font-semibold text-[#122033]">Request a Quote</h3>
            <p className="text-[10px] text-slate-400 mt-0.5">{clinicName}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="quote-modal-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* P0-8: Success state with confirmation + redirect */}
        {step === 'success' ? (
          <div className="px-5 py-8 text-center" data-testid="quote-success">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <Check className="h-6 w-6 text-emerald-600" />
            </div>
            <h4 className="text-base font-semibold text-[#122033] mb-2">Your request has been sent to the clinic</h4>
            <p className="text-xs text-slate-500 mb-1">
              {clinicName} will review your request and respond shortly.
            </p>
            {selectedFileIds.length > 0 && (
              /* P1-2 — a medical file is only ever readable by the named
                 person the patient chooses, so nothing is shared yet at
                 request time. Say so honestly instead of claiming a share. */
              <p className="text-xs text-slate-500 mb-1" data-testid="quote-medical-pending-note">
                {selectedFileIds.length} medical file(s) are ready to share. Once the
                clinic assigns your consultant you can choose exactly who may open them
                from <span className="font-medium">Medical Files</span>.
              </p>
            )}
            {successData?.matched_clinics_count > 0 && (
              <p className="text-xs text-teal-600 mb-3">
                We also matched you with {successData.matched_clinics_count} other clinic(s).
              </p>
            )}
            <p className="text-[11px] text-slate-400 mb-4">You can track all your requests from your dashboard.</p>
            <div className="flex gap-2 justify-center">
              <Button size="sm" variant="outline" className="text-xs" onClick={onClose} data-testid="quote-done-btn">
                Close
              </Button>
              <Button
                size="sm"
                className="bg-[#122033] text-white text-xs"
                onClick={() => { onClose(); navigate('/patient/dashboard'); }}
                data-testid="quote-go-dashboard-btn"
              >
                Go to My Dashboard
              </Button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-4 space-y-3 max-h-[70vh] overflow-y-auto">
            {/* P0-2: Treatment Selection — locked when preselected */}
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Treatment of Interest</label>
              {resolvedTreatmentLabel ? (
                <div className="flex items-center gap-2" data-testid="quote-locked-treatment">
                  <span className="inline-flex items-center gap-1.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg px-3 py-2 text-sm font-medium" data-testid="quote-preselected-treatment">
                    <Check className="h-3.5 w-3.5" />
                    {resolvedTreatmentLabel}
                  </span>
                  {/* Only show Change if NOT preselected from context */}
                  {!preselectedTreatment && (
                    <button
                      className="text-xs text-slate-400 hover:text-slate-600 underline"
                      onClick={() => { setResolvedTreatmentLabel(''); setTreatment(''); }}
                      data-testid="quote-change-treatment-btn"
                    >
                      Change
                    </button>
                  )}
                </div>
              ) : (
                <select
                  value={treatment}
                  onChange={e => setTreatment(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#122033] focus:ring-1 focus:ring-[#3BA4C5] focus:outline-none"
                  data-testid="quote-treatment-select"
                >
                  <option value="">Select a treatment (optional)</option>
                  {treatmentOptions.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              )}
            </div>

            {/* Message */}
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Your Message</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Describe what you're looking for — treatment goals, timeline, questions..."
                rows={4}
                maxLength={2000}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#122033] placeholder:text-slate-300 resize-none focus:ring-1 focus:ring-[#3BA4C5] focus:outline-none"
                data-testid="quote-message-input"
              />
              <span className="text-[9px] text-slate-300 float-right">{message.length}/2000</span>
            </div>

            {/* P0-6: Multi-select Contact Preference */}
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">Preferred Contact Methods</label>
              <div className="flex gap-2" data-testid="quote-contact-options">
                {CONTACT_OPTIONS.map(opt => {
                  const isSelected = contactPrefs.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => toggleContact(opt.value)}
                      className={`flex-1 px-2.5 py-1.5 rounded-lg text-xs border transition-all flex items-center justify-center gap-1 ${
                        isSelected
                          ? 'border-[#3BA4C5] bg-[#3BA4C5]/10 text-[#3BA4C5] font-medium'
                          : 'border-slate-200 text-slate-400 hover:border-slate-300'
                      }`}
                      data-testid={`quote-contact-${opt.value}`}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Medical Files Selection */}
            {token && !filesLoading && medicalFiles.length > 0 && (
              <div data-testid="medical-files-section">
                <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">
                  Share Medical Files (Optional)
                </label>
                <div className="border border-slate-200 rounded-lg p-2.5 space-y-1.5 max-h-36 overflow-y-auto bg-slate-50/50">
                  {medicalFiles.map(f => {
                    const selected = selectedFileIds.includes(f.attachment_id);
                    return (
                      <label
                        key={f.attachment_id}
                        className={`flex items-center gap-2.5 p-1.5 rounded-md cursor-pointer transition-colors ${
                          selected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-100 border border-transparent'
                        }`}
                        data-testid={`file-select-${f.attachment_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleFile(f.attachment_id)}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5"
                        />
                        {f.thumbnail_url ? (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${f.thumbnail_url}`}
                            alt={f.file_name}
                            className="h-8 w-8 rounded object-cover border border-slate-200 shrink-0"
                            loading="lazy"
                            data-testid={`file-thumb-${f.attachment_id}`}
                          />
                        ) : ['jpg', 'jpeg', 'png'].includes(f.file_type) ? (
                          <FileImage className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                        ) : (
                          <FileText className="h-3.5 w-3.5 text-red-500 shrink-0" />
                        )}
                        <span className="text-xs text-slate-700 truncate flex-1">{f.file_name}</span>
                        <Badge variant="outline" className="text-[9px] shrink-0">{f.file_type?.toUpperCase()}</Badge>
                      </label>
                    );
                  })}
                </div>
                {selectedFileIds.length > 0 && (
                  <div className="flex items-center gap-1 mt-1">
                    <Shield className="h-3 w-3 text-emerald-500" />
                    <span className="text-[10px] text-emerald-600" data-testid="quote-medical-selected-note">{selectedFileIds.length} file(s) flagged for {clinicName} — you choose who can open them after they assign your consultant</span>
                  </div>
                )}
                <p className="text-[9px] text-slate-400 mt-0.5">
                  Flagging files helps the clinic prepare — nobody can open a file until you
                  pick the exact person who may access it.
                </p>
              </div>
            )}

            {token && !filesLoading && medicalFiles.length === 0 && (
              <div className="text-[10px] text-slate-400 py-1">
                <a href="#/patient/medical-files" className="text-blue-500 hover:underline" data-testid="upload-files-link">
                  Upload medical files
                </a> to share them with clinics for better offers.
              </div>
            )}

            {/* Submit or Auth CTA */}
            {token ? (
              <Button
                className="w-full bg-[#3BA4C5] hover:bg-[#2d8aa8] text-white gap-1.5 mt-1"
                disabled={step === 'sending' || !message.trim() || message.trim().length < 5}
                onClick={handleSubmit}
                data-testid="quote-submit-btn"
              >
                {step === 'sending' ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending...</>
                ) : (
                  <><Send className="h-3.5 w-3.5" /> Send Quote Request</>
                )}
              </Button>
            ) : (
              /* P0-7: Clear register/login CTA when not logged in */
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/80 mt-1" data-testid="quote-auth-cta">
                <p className="text-sm font-medium text-[#122033] text-center mb-1">Create an account to receive offers</p>
                <p className="text-[10px] text-slate-400 text-center mb-3">Sign up to send your request and track clinic responses</p>
                <div className="flex gap-2">
                  <Button
                    className="flex-1 bg-[#3BA4C5] hover:bg-[#2d8aa8] text-white text-xs gap-1.5"
                    onClick={() => {
                      savePendingRequest();
                      navigate('/patient/register', { state: { returnTo: `/clinics/${clinicId}`, requestQuote: true } });
                      onClose();
                    }}
                    data-testid="quote-signup-btn"
                  >
                    <UserPlus className="h-3.5 w-3.5" /> Sign Up
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 text-xs gap-1.5"
                    onClick={() => {
                      savePendingRequest();
                      navigate('/login', { state: { returnTo: `/clinics/${clinicId}`, requestQuote: true } });
                      onClose();
                    }}
                    data-testid="quote-login-btn"
                  >
                    <LogIn className="h-3.5 w-3.5" /> Log In
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* Compact CTA button for use in search cards / profile header */
export function QuoteRequestButton({ clinicId, clinicName, preselectedTreatment, size = 'default', className = '' }) {
  const [showModal, setShowModal] = useState(false);
  const [sent, setSent] = useState(false);

  return (
    <>
      <Button
        size={size === 'small' ? 'sm' : 'default'}
        className={`gap-1.5 ${sent ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#3BA4C5] hover:bg-[#2d8aa8]'} text-white ${className}`}
        onClick={(e) => { e.stopPropagation(); setShowModal(true); }}
        disabled={sent}
        data-testid={`quote-cta-${clinicId}`}
      >
        {sent ? (
          <><Check className="h-3.5 w-3.5" /> Sent</>
        ) : (
          <><MessageCircle className="h-3.5 w-3.5" /> {size === 'small' ? 'Quote' : 'Request a Quote'}</>
        )}
      </Button>
      {showModal && (
        <QuoteRequestModal
          clinicId={clinicId}
          clinicName={clinicName}
          preselectedTreatment={preselectedTreatment}
          onClose={() => setShowModal(false)}
          onSuccess={() => setSent(true)}
        />
      )}
    </>
  );
}

/**
 * P0-1: Hook to handle pending quote requests after login.
 * Call this from the login page or app root after authentication succeeds.
 */
// FIND-H-08 final closure: token argument removed — cookie-based
// auth is applied automatically by the axios instance.
export async function submitPendingQuoteRequest() {
  const raw = localStorage.getItem('treatx_pending_quote');
  if (!raw) return null;
  try {
    const pending = JSON.parse(raw);
    if (Date.now() - pending.timestamp > 3600000) {
      localStorage.removeItem('treatx_pending_quote');
      return null;
    }
    const { data } = await api.post(`/public/clinics/${pending.clinic_id}/request-quote`, {
      message: pending.message || 'I would like to request a quote for this treatment.',
      treatment_interest: pending.treatment || undefined,
      preferred_contact: pending.contact_prefs || ['platform', 'email', 'phone'],
    }, {
    });
    localStorage.removeItem('treatx_pending_quote');
    return { ...data, clinic_name: pending.clinic_name };
  } catch {
    localStorage.removeItem('treatx_pending_quote');
  }
  return null;
}
